import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

/**
 * Rutas donde NO se ofrece instalar: acceso y alta, y las páginas públicas que ve quien todavía no es usuario.
 * Medido en producción por QA2 (16-oct, 390×844): el aviso cubría la casilla de privacidad, «Atrás» y «Continuar» de /signup y
 * «Recibir cotizacion» de la calculadora, sin scroll que los liberara. Quien está firmando o cotizando no es a quien se le ofrece la app.
 * El resto (el panel) sí lo muestra. Cada ruta de publicRoutes.tsx/authRoutes.tsx tiene que estar aquí: lo ata el test.
 */
export const RUTAS_SIN_AVISO_DE_INSTALACION: readonly string[] = [
  '/login',
  '/admin/login',
  '/register',
  '/signup',
  '/forgot-password',
  '/email-verification',
  '/verificar-email',
  '/onboarding',
  '/pagar',
  '/book',
  '/aceptar-invitacion',
  '/borrado-datos',
  '/network/invite',
  '/n/',
  '/metabroker',
  '/mortgage',
  '/properties',
  '/contact',
  '/pricing',
];

export const debeOfrecerInstalar = (pathname: string): boolean => {
  if (pathname === '/' || pathname === '') return false;
  return !RUTAS_SIN_AVISO_DE_INSTALACION.some(
    (p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`),
  );
};

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 7;

const isWithinCooldown = (): boolean => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) return false;
    const elapsedDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return elapsedDays < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
};

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isStandalone() || isWithinCooldown()) return;

    const handler = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'dismissed') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch {
      // ignore prompt errors
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible || !deferred || !debeOfrecerInstalar(pathname)) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalar InboMarket"
      className="fixed bottom-4 left-4 right-4 z-50 rounded-lg border border-slate-200 bg-white p-4 shadow-lg sm:left-auto sm:max-w-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
          <Download size={20} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Instalar InboMarket
          </h3>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Accede más rápido desde tu escritorio o pantalla de inicio. Funciona offline.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleInstall}
              className="min-h-[44px] rounded-md bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              Instalar
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="min-h-[44px] rounded-md px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Ahora no
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Cerrar"
          className="-m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
